// Square Payment Service for generating payment links
// Uses Square Payment Links API to create secure payment URLs

export interface PaymentLinkData {
  orderId: string
  orderDescription: string
  amount: number // in dollars
  customerEmail: string
  customerName?: string
}

export interface PaymentLink {
  id: string
  url: string
  longUrl: string
  createdAt: string
  orderId: string
}

class SquarePaymentService {
  private readonly SQUARE_ACCESS_TOKEN = import.meta.env.VITE_SQUARE_ACCESS_TOKEN
  private readonly SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID
  private readonly SQUARE_ENVIRONMENT = import.meta.env.VITE_SQUARE_ENVIRONMENT || 'sandbox'
  private readonly STORAGE_KEY = 'salty_mermaid_payment_links'

  /**
   * Check if Square is configured
   */
  isConfigured(): boolean {
    return !!(this.SQUARE_ACCESS_TOKEN && this.SQUARE_LOCATION_ID)
  }

  /**
   * Get Square API base URL
   */
  private getApiUrl(): string {
    return this.SQUARE_ENVIRONMENT === 'production'
      ? 'https://connect.squareup.com/v2'
      : 'https://connect.squareupsandbox.com/v2'
  }

  /**
   * Generate a payment link for an order
   * Calls Netlify function to avoid CORS issues
   */
  async createPaymentLink(data: PaymentLinkData): Promise<PaymentLink> {
    if (!this.isConfigured()) {
      throw new Error('Square is not configured. Please set VITE_SQUARE_ACCESS_TOKEN and VITE_SQUARE_LOCATION_ID')
    }

    try {
      // Determine the API endpoint based on environment
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const apiEndpoint = isLocalhost
        ? 'http://localhost:8888/.netlify/functions/create-payment-link' // Local Netlify Dev
        : '/.netlify/functions/create-payment-link' // Production

      console.log('🔗 Calling payment link creation endpoint:', apiEndpoint)

      // Call Netlify function to create payment link
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Payment link creation error:', errorData)
        throw new Error(`Payment link creation failed: ${errorData.error || 'Unknown error'}`)
      }

      const result = await response.json()
      console.log('✅ Square payment link created:', result)

      const paymentLink: PaymentLink = {
        id: result.id,
        url: result.url,
        longUrl: result.longUrl,
        createdAt: result.createdAt,
        orderId: data.orderId
      }

      // Store payment link locally
      this.savePaymentLink(paymentLink)

      return paymentLink
    } catch (error) {
      console.error('❌ Error creating Square payment link:', error)
      throw error
    }
  }

  /**
   * Save payment link to localStorage
   */
  private savePaymentLink(paymentLink: PaymentLink): void {
    try {
      const links = this.getPaymentLinks()
      links.push(paymentLink)
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(links))
    } catch (error) {
      console.error('Error saving payment link:', error)
    }
  }

  /**
   * Get all payment links
   */
  getPaymentLinks(): PaymentLink[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error loading payment links:', error)
      return []
    }
  }

  /**
   * Get payment link by order ID
   */
  getPaymentLinkByOrderId(orderId: string): PaymentLink | null {
    const links = this.getPaymentLinks()
    return links.find(link => link.orderId === orderId) || null
  }

  /**
   * Generate a simple payment URL (fallback method without API)
   * This creates a manual Square checkout link
   */
  generateSimplePaymentUrl(amount: number, description: string, orderId: string): string {
    // Format: https://squareup.com/pay/LOCATION_ID/ITEM_ID
    // For now, we'll generate a link to the general Square payment page
    // The merchant will need to manually create the invoice
    const encodedDescription = encodeURIComponent(description)
    const amountInCents = Math.round(amount * 100)

    // This is a placeholder - in production, you'd want to use the actual Square Payment Links API
    return `https://checkout.square.site/${this.SQUARE_LOCATION_ID}?amount=${amountInCents}&description=${encodedDescription}&reference=${orderId}`
  }

  /**
   * Create a manual invoice link (simpler approach)
   * This generates a mailto link with invoice details that the shop can use
   */
  generateInvoiceEmailLink(data: PaymentLinkData): string {
    const subject = `Payment Request for Order ${data.orderId}`
    const body = `Dear ${data.customerName || 'Customer'},

Thank you for your order!

Order Details:
- Order ID: ${data.orderId}
- Description: ${data.orderDescription}
- Amount: $${data.amount.toFixed(2)}

Please use this link to complete your payment securely through Square:
[Payment link will be inserted here]

If you have any questions, please contact us at info@saltymermaidart.com

Best regards,
The Salty Mermaid Art Studio`

    return `mailto:${data.customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }
}

// Export singleton instance
export const squarePaymentService = new SquarePaymentService()
